import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { useRaffleCurrentQuery } from "Api/Queries/Raffle/useRaffleCurrentQuery";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Raffle.Dashboard;

/**
 * Total tickets across the market's running competitions and the nearest announced draw (spec §8).
 * Renders nothing unless loaded and running: a marketing card must never block the dashboard.
 */
export const RaffleDashboardCard = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { data, isError, isPending } = useRaffleCurrentQuery();

  if (isPending || isError || !data?.isRunning) {
    return null;
  }

  // Date-only yyyy-MM-dd strings are sorted as text and formatted as-is: a Date would shift the
  // day west of UTC.
  const nearestDrawDate = data.competitions
    .flatMap((competition) =>
      competition.isAnnounced && competition.drawDate
        ? [competition.drawDate]
        : [],
    )
    .toSorted()[0];

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <ConfirmationNumberIcon color="primary" fontSize="large" />
            <Stack>
              <Typography variant="subtitle1">{t(RESOURCES.Title)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(RESOURCES.TicketCount)}: <b>{data.totalMyTicketCount}</b>
                {/* One template literal, so JSX whitespace collapsing keeps the spaces. */}
                {` · ${t(RESOURCES.DrawDate)}: ${
                  nearestDrawDate
                    ? formatDate(nearestDrawDate)
                    : t(RESOURCES.DateToBeAnnounced)
                }`}
              </Typography>
              {data.competitions.length > 1 && (
                <Typography variant="body2" color="text.secondary">
                  {t(RESOURCES.CompetitionsRunning, {
                    count: data.competitions.length,
                  })}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Button size="small" onClick={() => navigate(appUrl("raffle"))}>
            {t(RESOURCES.ShowDetail)}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
